import {
  APPROACH,
  NEEDLE_GAUGE,
  number,
  OTHER_TEXT,
  option,
  type ProcedureDefinition,
  routine,
  SHARED_FIELD_IDS,
  SHARED_FIELDS,
  single,
  text,
  YES_NO,
  YES_PARTIAL_NO,
} from "./procedure-fields";

const NERVE_BLOCK_CODES = ["64450", "76942"];

const NERVES = [
  option("suprascapular", "Suprascapular"),
  option("lateral_femoral_cutaneous", "Lateral femoral cutaneous"),
  option("saphenous", "Saphenous"),
  option("sural", "Sural"),
  option("superficial_peroneal", "Superficial peroneal"),
  option("radial", "Radial (forearm/wrist)"),
  option("median", "Median (forearm/wrist)"),
  option("ulnar", "Ulnar (forearm/wrist)"),
  OTHER_TEXT,
];

const painScore = (id: string, label: string) =>
  number(id, label, "/10", { integer: true, min: 0, max: 10 });

export const GUIDED_NERVE_BLOCK: ProcedureDefinition = {
  examType: "guided_nerve_block",
  version: 1,
  allNormal: true,
  indications: [
    option("acute_pain", "Acute pain / analgesia"),
    option("chronic_pain_diagnostic", "Chronic pain, diagnostic block"),
    option("chronic_pain_therapeutic", "Chronic pain, therapeutic block"),
    option("procedural_anesthesia", "Procedural anesthesia"),
    option("other", "Other"),
  ],
  views: [
    option("pre_target_short_axis", "Pre-procedure target nerve, short axis"),
    option("pre_target_long_axis", "Pre-procedure target nerve, long axis"),
    option("doppler", "Doppler"),
    option("needle_to_nerve_in_plane", "Needle-to-nerve, in-plane"),
    option("needle_to_nerve_out_of_plane", "Needle-to-nerve, out-of-plane"),
    option("perineural_spread", "Perineural spread"),
  ],
  fields: [
    ...SHARED_FIELDS,
    single("nerve", "Nerve targeted", NERVES),
    single(
      "nerve_identified",
      "Nerve identified on ultrasound",
      YES_PARTIAL_NO,
    ),
    APPROACH,
    NEEDLE_GAUGE,
    single(
      "needle_tip_visualized",
      "Needle tip visualized adjacent to nerve",
      YES_PARTIAL_NO,
    ),
    single(
      "negative_aspiration",
      "Negative aspiration before injection",
      YES_NO,
    ),
    single(
      "perineural_spread_visualized",
      "Perineural spread visualized",
      YES_PARTIAL_NO,
    ),
    single(
      "paresthesia_or_high_pressure",
      "Paresthesia or high injection pressure",
      YES_NO,
    ),
    text(
      "local_anesthetic",
      "Local anesthetic (drug / concentration / volume)",
    ),
    text("adjunct", "Adjunct (drug and dose)"),
    painScore("pain_score_before", "Pain score before"),
    painScore("pain_score_after", "Pain score after"),
    single("block_effect", "Block effect", [
      option("effective", "Effective"),
      option("partial", "Partial"),
      option("not_effective", "Not effective"),
      option("not_assessed", "Not assessed"),
    ]),
    routine(
      "immediate_complications",
      "Immediate complications",
      [
        option("none", "None"),
        option("vascular_puncture", "Vascular puncture"),
        option(
          "local_anesthetic_toxicity",
          "Signs of local anesthetic toxicity",
        ),
        option("nerve_symptoms", "Nerve symptoms"),
        option("vasovagal", "Vasovagal"),
        OTHER_TEXT,
      ],
      "none",
    ),
  ],
  siteFieldId: "nerve",
  needleTipFieldId: "needle_tip_visualized",
  completeWhenAnswered: [...SHARED_FIELD_IDS, "nerve", "needle_tip_visualized"],
  codesBySite: Object.fromEntries(
    NERVES.map((nerve) => [nerve.value, NERVE_BLOCK_CODES]),
  ),
};
